import { ArpMediaType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Column, Row, Text } from '../../core/Components'
import { ChevronDownIcon, ChevronUp } from '../../core/icons'
import MoreInfo from '../../core/MoreInfo'
import useSelectedMedia from '../new-explore/hooks/useSelectedMedia'
import { GLOBAL_PLAYER_TABLET_HEIGHT } from './Common'
import MediaDNABody from './MediaDNABody'

const borderRadius = '10px 10px 0px 0px'

const MediaDNAContainer = styled(Column, {
  zIndex: 16,
  display: 'none',
  position: 'fixed',
  right: 0,
  bottom: GLOBAL_PLAYER_TABLET_HEIGHT,
  width: '390px',
  borderRadius,
  boxShadow: '0px 0px 50.1354px rgba(6, 19, 63, 0.2)',
  clipPath: 'inset(-100% 0px 0px -100%)',
  overflowY: 'hidden',
  '@tablet': {
    display: 'flex'
  },
  variants: {
    isExpanded: {
      true: { animation: 'appear 200ms ease' }
    }
  }
})

const MediaDNAHeader = styled(Row, {
  height: '50px',
  borderRadius,
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px 20px',
  cursor: 'pointer',
  transition: '100ms',
  '&:hover': {
    filter: 'brightness(90%)'
  },
  variants: {
    type: {
      song: {
        background: 'linear-gradient(91.35deg, #787DFA 20.64%, #8BAAF6 87.79%)'
      },
      sound: {
        background: 'linear-gradient(91.35deg, #E567E8 20.64%, #F38686 87.79%)'
      }
    }
  }
})

const HeaderIcon: FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const css = { height: '24px', width: '24px' }
  return (
    <Column css={{ color: '$gray005' }} verticalCenter>
      {isExpanded && <ChevronDownIcon style={css} />}
      {!isExpanded && <ChevronUp style={css} />}
    </Column>
  )
}

const useMediaDNA = () => {
  const { globalPlayer } = useTreeState()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [media, setMedia] = useState<IArpMedia | undefined>(undefined)
  const { media: selectedMedia } = useSelectedMedia()

  const toggleCollapse = () => setIsExpanded(!isExpanded)

  useEffect(() => {
    if (globalPlayer.dnaIsLocked) return

    setMedia(selectedMedia)
  }, [selectedMedia])

  return { isExpanded, toggleCollapse, type: media?.type, media, setMedia }
}

const MediaDNA = () => {
  const state = useMediaDNA()

  if (!state.media) return
  return (
    <MediaDNAContainer isExpanded={state.isExpanded}>
      <MediaDNAHeader
        onClick={state.toggleCollapse}
        type={state.type === ArpMediaType.SONG ? 'song' : 'sound'}
      >
        <Row css={{ color: '$gray005' }} verticalCenter>
          <Text css={{ margin: '0', paddingRight: '5px' }}>
            {state.type === ArpMediaType.SONG ? 'Song DNA' : 'Remixes'}
          </Text>
          <MoreInfo
            tooltipText={
              state.type === ArpMediaType.SONG
                ? 'Browse the sample and stems used to create this track'
                : 'Browse the library of songs that have used this sound'
            }
          />
        </Row>
        <HeaderIcon isExpanded={state.isExpanded} />
      </MediaDNAHeader>
      <MediaDNABody isExpanded={state.isExpanded} media={state.media} />
    </MediaDNAContainer>
  )
}

export default observer(MediaDNA)
